import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowUpRight, Loader2, Repeat } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { PayNowButton, formatMoney } from '../../components/dtp/PortalOrderParts';
import { ConsentCheckbox, REFILL_INTERVALS, refillApiError, refillDate } from '../../components/dtp/AutoRefillSheet';
import type { RefillIntervalDays, RefillPlan, RefillPlansResponse } from '../../types/refills';

/**
 * Manage auto-refills. A plan prepares an order and texts a payment link on
 * its schedule; nothing here charges a card. A clinic viewing the portal sees
 * the plans but cannot change them (the API refuses too).
 */
export function DtpRefills() {
  const { physicianId } = useDtpParams();
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const plans = useQuery<RefillPlansResponse>({
    queryKey: ['refill-plans', physicianId, patientId],
    retry: false,
    queryFn: async () => {
      const res = await dtpApi.getRefillPlans();
      if (!res.success || !res.data) throw new Error('Refills unavailable');
      return res.data;
    },
  });
  const live = plans.data?.plans.filter((p: RefillPlan) => p.status !== 'cancelled') || [];

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="portal-display text-[36px] font-bold leading-none sm:text-[44px]">Auto-refills</h1>
      <p className="mt-3 text-[17px] text-[var(--portal-muted)]">We prepare your order on schedule and text you a link to pay. Nothing is charged automatically.</p>
      {impersonated && <p className="mt-4 rounded-2xl bg-[var(--portal-tint-2)] p-3 text-sm">You’re viewing this portal for the patient. Only the patient can change auto-refills.</p>}

      {plans.isPending ? (
        <div role="status" aria-label="Loading auto-refills" className="mt-8 space-y-4">
          {[0, 1].map(i => <div key={i} className="portal-card h-40 motion-safe:animate-pulse" />)}
        </div>
      ) : plans.isError ? (
        <div role="alert" className="mt-8 rounded-2xl bg-[#fde4dc] p-4 text-sm text-[#8c2a14]">
          We couldn’t load your auto-refills. <button className="min-h-[44px] font-semibold underline" onClick={() => plans.refetch()}>Try again</button>
        </div>
      ) : live.length === 0 ? (
        <div className="portal-card mt-8 px-6 py-16 text-center">
          <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-[18px] bg-[var(--portal-tint-1)]"><Repeat aria-hidden="true" className="h-6 w-6" /></span>
          <p className="portal-display text-xl font-semibold">No auto-refills yet</p>
          <p className="mt-2 text-sm text-[var(--portal-muted)]">Set one up from a delivered order and never run out.</p>
          <Link to={dtpPath(physicianId, '/orders')} className="mt-6 inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90">Go to my orders<ArrowUpRight aria-hidden="true" className="h-4 w-4" /></Link>
        </div>
      ) : (
        <ol className="mt-8 space-y-4">
          {live.map((plan: RefillPlan) => <li key={plan._id}><RefillPlanCard plan={plan} requiredConsents={plans.data.requiredConsents} readOnly={impersonated} /></li>)}
        </ol>
      )}
    </div>
  );
}

type Action = 'pause' | 'resume' | 'skip' | 'cancel' | 'interval';

function RefillPlanCard({ plan, requiredConsents, readOnly }: { plan: RefillPlan; requiredConsents: string[]; readOnly: boolean }) {
  const queryClient = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(plan.pauseReason === 'consent_needed');
  const [agreed, setAgreed] = useState(false);
  const act = useMutation({
    mutationFn: async ({ action, intervalDays }: { action: Action; intervalDays?: RefillIntervalDays }) => {
      const res = action === 'pause' ? await dtpApi.pauseRefillPlan(plan._id)
        : action === 'resume' ? await dtpApi.resumeRefillPlan(plan._id, needsConsent ? requiredConsents : undefined)
        : action === 'skip' ? await dtpApi.skipNextRefill(plan._id)
        : action === 'cancel' ? await dtpApi.cancelRefillPlan(plan._id)
        : await dtpApi.setRefillInterval(plan._id, intervalDays!);
      if (!res.success) throw new Error(res.error || 'Something went wrong');
      return res.data;
    },
    onSuccess: () => {
      setConfirmCancel(false); setNeedsConsent(false); setAgreed(false);
      queryClient.invalidateQueries({ queryKey: ['refill-plans'] });
      queryClient.invalidateQueries({ queryKey: ['refill-eligibility'] });
    },
    onError: (error) => {
      if (refillApiError(error, '').code === 'consent_needed') setNeedsConsent(true);
    },
  });
  const error = act.error ? refillApiError(act.error, 'We couldn’t update this auto-refill. Please try again.') : null;
  const paused = plan.status === 'paused';
  const busy = act.isPending;
  const pill = 'inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold transition disabled:opacity-50';

  return (
    <article aria-labelledby={`plan-${plan._id}`} className="portal-card p-6 sm:p-7">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={`plan-${plan._id}`} className="portal-display text-xl font-bold">{plan.items.map(i => i.productName).join(', ')}</h2>
          <p className="mt-0.5 text-sm text-[var(--portal-muted)]">
            Every {plan.intervalDays} days{plan.estimatedSubtotal !== undefined && <> · about {formatMoney(plan.estimatedSubtotal)} + shipping at today’s price</>}
          </p>
        </div>
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${paused ? 'bg-[#fde4dc] text-[#8c2a14]' : 'bg-[#dcf2e3] text-[#14532d]'}`}>{paused ? 'Paused' : 'Active'}</span>
      </header>

      {paused && plan.pauseMessage && (
        <p className={`mt-4 flex items-start gap-2 rounded-2xl p-3 text-sm ${plan.pausedBy === 'system' ? 'bg-[#fde4dc] text-[#8c2a14]' : 'bg-[var(--portal-tint-2)]'}`}>
          {plan.pausedBy === 'system' && <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />}{plan.pauseMessage}
        </p>
      )}

      {!paused && <p className="mt-4 text-[15px]">Next refill prepared on <strong>{refillDate(plan.nextRunAt)}</strong>. We’ll text you a link to pay.</p>}

      {plan.lastOrder?.payUrl && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[var(--portal-tint-1)] p-4">
          <p className="text-[15px]"><span className="font-semibold">Refill #{plan.lastOrder.orderNumber} is ready.</span> <span className="text-[var(--portal-muted)]">It ships once you pay.</span></p>
          <PayNowButton order={plan.lastOrder} />
        </div>
      )}

      {!readOnly && (
        <div className="mt-5 space-y-4 border-t border-[var(--portal-line)] pt-5">
          <fieldset disabled={busy}>
            <legend className="text-sm font-semibold">How often</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {REFILL_INTERVALS.map(days => (
                <button key={days} type="button" aria-pressed={plan.intervalDays === days}
                  onClick={() => plan.intervalDays !== days && act.mutate({ action: 'interval', intervalDays: days })}
                  className={`h-10 rounded-full px-4 text-sm font-semibold ring-1 transition ${plan.intervalDays === days ? 'bg-[var(--portal-ink)] text-white ring-transparent' : 'bg-white/80 ring-[var(--portal-line)] hover:bg-white'}`}>
                  {days} days
                </button>
              ))}
            </div>
          </fieldset>

          {needsConsent && <ConsentCheckbox consents={requiredConsents} checked={agreed} onChange={setAgreed} />}
          {error && <p role="alert" className="rounded-2xl bg-[#fde4dc] p-3 text-sm text-[#8c2a14]">{error.message}</p>}

          <div className="flex flex-wrap gap-2">
            {paused ? (
              <button type="button" disabled={busy || (needsConsent && !agreed)} onClick={() => act.mutate({ action: 'resume' })}
                className={`${pill} bg-[var(--portal-primary)] text-[var(--portal-primary-fg)] hover:opacity-90`}>
                {busy && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}Turn back on
              </button>
            ) : (
              <>
                <button type="button" disabled={busy} onClick={() => act.mutate({ action: 'skip' })} className={`${pill} bg-white/80 ring-1 ring-[var(--portal-line)] hover:bg-white`}>Skip next refill</button>
                <button type="button" disabled={busy} onClick={() => act.mutate({ action: 'pause' })} className={`${pill} bg-white/80 ring-1 ring-[var(--portal-line)] hover:bg-white`}>Pause</button>
              </>
            )}
            {confirmCancel ? (
              <span className="flex flex-wrap items-center gap-2">
                <button type="button" disabled={busy} onClick={() => act.mutate({ action: 'cancel' })} className={`${pill} bg-[#8c2a14] text-white hover:opacity-90`}>Yes, cancel auto-refill</button>
                <button type="button" onClick={() => setConfirmCancel(false)} className={`${pill} hover:bg-white/80`}>Keep it</button>
              </span>
            ) : (
              <button type="button" disabled={busy} onClick={() => setConfirmCancel(true)} className={`${pill} text-[#8c2a14] hover:bg-white/80`}>Cancel</button>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
